import { ref } from "vue";
import type { Ref } from "vue";
import { defineStore } from "pinia";
import type { Reward } from "@/models/reward";
import { RewardApiService } from "@/services/rewardApiService";
import { useGtm } from "@gtm-support/vue-gtm";
import { useCardsStore } from "@/stores/cards";
import * as amplitude from "@amplitude/analytics-browser";
import { useCategoryStore } from "@/stores/categories";
import router from '@/router'

export const useRewardsStore = defineStore('rewards', () => {
  // State
  const rewardsLoaded = ref<boolean>(false);
  const rewards = ref<Reward[] | undefined>(undefined);
  const searchQuery = ref<string>('');
  const searchCategory = ref<string>('');
  const searchResults = ref<Reward[] | undefined>(undefined);
  const selectedReward = ref<Reward | undefined>(undefined);
  const featuredRewards = ref<Reward[] | undefined>(undefined);
  const isRewardModalShowing = ref<boolean>(false);
  const stateChangeIndicator = ref<boolean>(false);

  // Getters
  function getRewards(limit?: number): Reward[] {
    if (limit) {
      return rewards.value?.slice(0, limit) ?? [];
    }
    return rewards.value ?? [];
  }

  function getFeaturedRewards(): Reward[] {
    return featuredRewards.value ?? [];
  }

  function getSearchResults(): Reward[] {
    return searchResults.value ?? [];
  }

  function getSelectedReward(): Reward | undefined {
    return selectedReward.value;
  }

  function getRewardsLoaded(): boolean {
    return rewardsLoaded.value;
  }

  function getSearchQuery(): Ref<string> {
    return searchQuery;
  }

  function getSearchCategory(): Ref<string> {
    return searchCategory;
  }

  function getStateChangeIndicator(): Ref<boolean> {
    return stateChangeIndicator;
  }

  // Actions
  async function fetchRewards(isHome?: boolean) {
    if (rewardsLoaded.value) {
      return;
    }

    // Grab regular rewards
    const response = await RewardApiService.get({
      pageSize: 20,
      pageNumber: 1,
      featured: 0,
    });

    rewards.value = response?.data?.items;

    // Grab featured rewards
    const featuredResponse = await RewardApiService.get({
      pageSize: 20,
      pageNumber: 1,
      featured: 1,
    });

    featuredRewards.value = featuredResponse?.data?.items;

    // Merge arrays
    rewards.value = rewards.value?.concat(featuredResponse?.data?.items ?? []);

    rewardsLoaded.value = true;
  }

  // Perform a search
  async function fetchSearchRewards(query: string, pageSize?: number) {
    const currentQuery = router.currentRoute?.value?.query;
    if (!query) {
      console.error("Query is undefined or null");
      return;
    }

    const searchParams: any = {
      pageSize: pageSize ?? 10,
      pageNumber: 1,
      search: query,
    };

    searchParams.search = query;

    const response = await RewardApiService.get(searchParams);
    searchResults.value = response?.data?.items;

    amplitude.track('Offer searched', {
      'Query': query,
    });
  }

  // Open the reward modal
  function openRewardModal(reward: Reward, location: string) {
    const gtm = useGtm();
    const cardsStore = useCardsStore();
    const categoryStore = useCategoryStore();
    // Trigger GTM event
    if (gtm) {
      gtm?.trackEvent({
        event: cardsStore.activeCards.length > 0 ? 'rewardClickedCardLinked' : 'rewardClickedNoCardLinked',
      });
    }
    amplitude.track('Offer Viewed', {
      'In-App Location': location,
      'Offer category': categoryStore.getCategoryByPlatformName(reward.category)?.name ?? reward.category,
      'Brand': reward.brand,
    });
    selectedReward.value = reward;
    isRewardModalShowing.value = true;
  }

  // Close the reward modal
  function closeRewardModal() {
    isRewardModalShowing.value = false;
  }

  // Set search query
  function setSearchQuery(query: string) {
    // Clear our search category
    searchCategory.value = '';
    // Update our search query
    searchQuery.value = query;
    // Flip our indicator to trigger a re-render
    stateChangeIndicator.value = !stateChangeIndicator.value;
  }

  // Set search category
  function setSearchCategory(category: string) {
    // Clear our search query
    searchQuery.value = '';
    // Update our search category
    searchCategory.value = category;
    // Flip our indicator to trigger a re-render
    stateChangeIndicator.value = !stateChangeIndicator.value;
  }

  function $reset() {
    // Stub
  }

  return {
    fetchRewards,
    fetchSearchRewards,
    getRewardsLoaded,
    getSelectedReward,
    isRewardModalShowing,
    getSearchQuery,
    getSearchCategory,
    setSearchQuery,
    setSearchCategory,
    openRewardModal,
    closeRewardModal,
    getRewards,
    getFeaturedRewards,
    getSearchResults,
    getStateChangeIndicator,
    featuredRewards,
    $reset,
  };
});
